/**
 * Maps candidate-placements API response to PlacementTracker PlacementData format.
 * Uses disciplineAbbr, specialtyAbbr, subSpecialtyAbbr for skill UI labels.
 * Maps PlacementActivityStatus to active/completed/pending/cancelled per enum logic.
 */
import type { PlacementData, CandidateInfo, PlacementRecord, FacilityInfo } from './PlacementTracker';

/**
 * Maps PlacementActivityStatus (by id or description) to PlacementTracker category.
 * active = OnAssignment (23)
 * completed = AssignmentComplete (24)
 * cancelled = Declined/Canceled statuses
 * pending = all other positive in-progress statuses
 */
function mapPlacementStatusToCategory(status: string | undefined, activityStatusId?: number): PlacementRecord['status'] {
  const normalized = (status || '').toLowerCase().replace(/\s+/g, '');
  if (['active', 'completed', 'pending', 'cancelled'].includes(normalized)) return normalized as PlacementRecord['status'];

  const id = activityStatusId ?? parseActivityStatusId(status);
  if (id !== undefined) {
    if (id === 23) return 'active';
    if (id === 24) return 'completed';
    if ([4, 5, 7, 20, 21, 22, 25, 26, 27, 28, 29].includes(id)) return 'cancelled';
  }

  if (
    /onassignment|onassignment/i.test(status || '') ||
    normalized.includes('onassignment')
  ) return 'active';
  if (
    /assignmentcomplete|assignment complete/i.test(status || '') ||
    normalized.includes('assignmentcomplete')
  ) return 'completed';
  if (
    /declined|canceled|cancelled|requestcancelled|settlement/i.test(status || '')
  ) return 'cancelled';

  return 'pending';
}

function parseActivityStatusId(status: string | undefined): number | undefined {
  if (!status) return undefined;
  const byDescription: Record<string, number> = {
    'waitingforclearance': 1, 'requestfileout': 2, 'sent': 3, 'declinedbytraveler': 4,
    'declinedbyfacility': 5, 'interviewed': 6, 'requestcancelled': 7, 'oktosend': 8,
    'offerbyfacility': 9, 'offerbookedbytraveler': 10, 'traveleraccepts': 11,
    'facilityconfirms': 12, 'booked': 13, 'bookingapproved': 14, 'waitinggoodtogo': 15,
    'interviewscheduled': 16, 'postponementwarning': 17, 'goodtogo': 18,
    'canceledbytraveler': 20, 'canceledbyfacility': 21, 'canceledbyamh': 22,
    'onassignment': 23, 'assignmentcomplete': 24, 'assignmentcanceledbytraveler': 25,
    'assignmentcanceledbyfacility': 26, 'assignmentcanceledbyamh': 27,
    'canceledbysettlement': 28, 'assignmentcanceledbysettlement': 29,
  };
  const key = status.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
  return byDescription[key];
}

export interface ApiSkillObject {
  disciplineId?: number;
  disciplineAbbr?: string;
  specialtyId?: number;
  specialtyAbbr?: string | null;
  subSpecialtyId?: number | null;
  subSpecialtyAbbr?: string | null;
  isVerified?: number | null;
}

export interface ApiCertification {
  certificationName?: string;
  expirationDate?: string | null;
  credentialName?: string;
  issuedBy?: string;
  verified?: number;
  hearsayLicense?: number;
}

export interface ApiRequiredSkill {
  disciplineId?: number;
  discipline?: string;
  disciplineAbbr?: string;
  specialtyId?: number;
  specialty?: string;
  specialtyAbbr?: string;
  subSpecialtyId?: number;
}

export interface ApiFacility {
  id: string;
  name: string;
  type?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phone?: string | null;
  openPositions?: number;
  requiredSkills?: ApiRequiredSkill[];
  website?: string;
  specialties?: { value?: string }[];
  contactEmail?: string;
  parRate?: number;
  payRate?: number;
  rating?: number | number[] | null;
  bedCount?: number | null;
  parkingInfo?: string;
  orientation?: string;
}

export interface ApiPlacement {
  id: string;
  status: string;
  activityStatusId?: number;
  activityTypeId?: number;
  role?: string;
  skills?: ApiSkillObject[];
  shift?: string;
  payRate?: number;
  startDate?: string | null;
  endDate?: string | null;
  orderPositions?: number;
  facility: ApiFacility;
}

export interface ApiCandidate {
  id: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  skills?: ApiSkillObject[];
  certifications?: ApiCertification[];
  experience?: number | null;
  rating?: number | null;
  availability?: string | null;
  preferredShift?: string | null;
  licensureState?: string | null;
  npiNumber?: string | null;
}

export interface ApiCandidatePlacementsResponse {
  candidate: ApiCandidate;
  placements: ApiPlacement[];
}

function formatSkillLabel(skill: ApiSkillObject | ApiRequiredSkill): string {
  const parts: string[] = [];
  if ('disciplineAbbr' in skill && skill.disciplineAbbr) parts.push(skill.disciplineAbbr);
  if ('specialtyAbbr' in skill && skill.specialtyAbbr) parts.push(skill.specialtyAbbr);
  if ('subSpecialtyAbbr' in skill && skill.subSpecialtyAbbr) parts.push(skill.subSpecialtyAbbr);
  return parts.length > 0 ? parts.join(' - ') : '';
}

function skillsToLabels(skills: ApiSkillObject[] | ApiRequiredSkill[] | undefined): string[] {
  if (!Array.isArray(skills)) return [];
  return skills
    .map(s => formatSkillLabel(s))
    .filter(Boolean);
}

function mapFacility(api: ApiFacility): FacilityInfo {
  const rating = Array.isArray(api.rating) ? 0 : (typeof api.rating === 'number' ? api.rating : 0);
  return {
    id: String(api.id),
    name: api.name || '',
    type: api.type || '',
    address: api.address || '',
    city: api.city || '',
    state: api.state || '',
    zipCode: api.zipCode || '',
    phone: api.phone || '',
    contactEmail: api.contactEmail || '',
    website: api.website || '',
    rating,
    bedCount: api.bedCount ?? 0,
    openPositions: api.openPositions ?? 0,
    requiredSkills: skillsToLabels(api.requiredSkills || []),
    specialties: (api.specialties || []).map(s => s?.value || '').filter(Boolean),
    parkingInfo: api.parkingInfo || '',
    orientation: api.orientation || '',
  };
}

function mapCandidate(api: ApiCandidate): CandidateInfo {
  return {
    id: String(api.id),
    firstName: api.firstName || '',
    lastName: api.lastName || '',
    title: api.title || '',
    email: api.email || '',
    phone: api.phone || '',
    address: api.address || '',
    city: api.city || '',
    state: api.state || '',
    zipCode: api.zipCode || '',
    skills: skillsToLabels(api.skills || []),
    certifications: [...new Set((api.certifications || []).map(c => c?.certificationName || '').filter(Boolean))],
    experience: api.experience ?? 0,
    rating: api.rating ?? 0,
    availability: api.availability || '',
    preferredShift: api.preferredShift || '',
    licensureState: api.licensureState || '',
    npiNumber: api.npiNumber || '',
  };
}

function mapPlacement(api: ApiPlacement): PlacementRecord {
  const mappedStatus = mapPlacementStatusToCategory(api.status, api.activityStatusId);
  const payRate =
    api.payRate ?? api.facility?.parRate ?? api.facility?.payRate ?? 0;
  return {
    id: String(api.id),
    status: mappedStatus,
    statusLabel: api.status || undefined,
    activityStatusId: api.activityStatusId,
    activityTypeId: api.activityTypeId,
    startDate: api.startDate ?? '',
    endDate: api.endDate ?? '',
    role: api.role || '',
    skills: skillsToLabels(api.skills || []),
    payRate: typeof payRate === 'number' ? payRate : Number(payRate) || 0,
    shift: api.shift || '',
    orderPositions: api.orderPositions,
    facility: mapFacility(api.facility),
  };
}

export function mapCandidatePlacementsApiToPlacementData(
  response: ApiCandidatePlacementsResponse,
): PlacementData {
  return {
    candidate: mapCandidate(response.candidate),
    placements: (response.placements || []).map(mapPlacement),
  };
}
